'use client';

import { useCallback, useEffect, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';

import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { useFeatureFlag } from '@/feature-flags/hooks/use-feature-flag.hook';
import { authStore, expireSessionIfTokenExpired } from '@/store/auth.store';
import { setOpenSignInModal } from '@/store/modal';
import { openPatientSelection } from '@/store/patient-selection/patient-selection.store';
import { PatientOption, ScheduleAgeRange } from '@/types/patient-selection.types';
import { AnalyticsScheduleType } from '@/types/enums/analytics/search/search-analytics.enum';
import {
  PurchaseJourney,
  PurchaseJourneyAnalyticsEnum,
  PurchaseJourneyAppointmentType,
} from '@/types/enums/analytics/purchase-journey/purchase-journey-analytics.enum';
import { getCartOrigin } from '@/utils/cart/get-cart-origin.util';
import { buildUrlWithPersistedParams } from '@/utils/persisted-params.util';
import { getLoginOrigin } from '@/utils/auth/get-login-origin.util';
import { onceAuthResolves } from '@/utils/auth/once-auth-resolves.util';

export interface PurchaseJourneyEntry {
  isOnline: boolean;
  isSlotChoice: boolean;
}

export interface PatientSelectionParams {
  journeyEntry?: PurchaseJourneyEntry;
  ageRange?: ScheduleAgeRange;
  scheduleType?: AnalyticsScheduleType;
  canSearchAnotherOption?: boolean;
  onBeforeSelection?: () => Promise<boolean>;
  onSelectPatient?: (patient: PatientOption) => void;
}

export interface UsePatientSelectionReturn {
  requestPatientSelection: (params: PatientSelectionParams) => void;
}

const IDENTIFICATION_PATH = '/identificacao/';

function trackSlotChosen(pathname: string, isOnline: boolean, isLoggedIn: boolean): void {
  getAnalyticsPlugin().track(PurchaseJourneyAnalyticsEnum.SLOT_CHOSEN, {
    origem: getCartOrigin(pathname),
    logado: isLoggedIn,
    tipo_atendimento: isOnline
      ? PurchaseJourneyAppointmentType.TELECONSULTA
      : PurchaseJourneyAppointmentType.PRESENCIAL,
    jornada: PurchaseJourney.UNIFIED,
  });
}

function buildIdentificationPath(): string {
  const retorno = `${window.location.pathname}${window.location.search}`;

  return buildUrlWithPersistedParams({
    baseUrl: `${IDENTIFICATION_PATH}?${new URLSearchParams({ retorno }).toString()}`,
  });
}

export function usePatientSelection(): UsePatientSelectionReturn {
  const pathname = usePathname();
  const router = useRouter();
  const { value: isPurchaseJourneyOn } = useFeatureFlag('newPurchaseJourney');
  const cancelPendingRequest = useRef<() => void>(() => {});

  useEffect(() => () => cancelPendingRequest.current(), []);

  const requestPatientSelection = useCallback(({
    journeyEntry,
    ageRange,
    scheduleType,
    canSearchAnotherOption,
    onBeforeSelection,
    onSelectPatient,
  }: PatientSelectionParams) => {
    cancelPendingRequest.current();

    const openSelection = async () => {
      if (onBeforeSelection && !(await onBeforeSelection())) {
        return;
      }

      openPatientSelection({
        ageRange: ageRange ?? {},
        scheduleType,
        canSearchAnotherOption,
        onSelectPatient,
      });
    };

    cancelPendingRequest.current = onceAuthResolves(() => {
      expireSessionIfTokenExpired();

      const { isLoggedIn } = authStore.getState().computed;
      const isJourneyEntry = isPurchaseJourneyOn && journeyEntry !== undefined;

      if (isJourneyEntry && journeyEntry.isSlotChoice) {
        trackSlotChosen(pathname, journeyEntry.isOnline, isLoggedIn);
      }

      if (!isLoggedIn && isJourneyEntry) {
        router.push(buildIdentificationPath());
        return;
      }

      if (!isLoggedIn) {
        setOpenSignInModal(true, {
          origin: getLoginOrigin(pathname),
          loginCallback: () => void openSelection(),
        });
        return;
      }

      void openSelection();
    });
  }, [pathname, router, isPurchaseJourneyOn]);

  return { requestPatientSelection };
}
